/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ITasksConfig, INotesConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { Redirect, RouteConfig } from 'aurelia-router';
import { ITaskBinding } from 'app/core/components/tasks/task';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { TaskService } from 'app/core/services/task-service';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class EditInterventionPage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    protected memberId!: string;
    protected caseId!: string;
    protected goalId!: string;
    protected interventionId!: string;
    protected taskBinding: ITaskBinding | null = null;

    protected mcgCopyright?: string;

    @bindable.string({ changeHandler: 'titleReload' })
    protected taskDisplay: string | null = null;

    protected routeConfig: RouteConfig | null = null;

    protected tasksConfig: ITasksConfig = {
        primaryTargetType: 'CM.CarePlanIntervention',
        serviceRootUrl: 'cm/careplans',
        taskServiceRootUrl: 'cm/cases',
        targetType: 'CM.CarePlanGoal',
        taskTargetType: 'CM.Case',
        labelKey: 'cm:carePlanIntervention.$',
        backRouteName: 'cm.case.careplan',
        addTargetProperty: this.addGoalProperty.bind(this),
        addTaskTargetProperty: this.addCaseProperty.bind(this),
        appointmentsConfig: {
            addTargetProperty: (appointment: CM.CarePlanInterventionAppointment) => { appointment.intervention = { id: this.interventionId }; }
        },
        smartTasksConfig: {
            moduleTaskCode: 'CM.Task', // for smart tasks like Refer to CM to use as Referral Origin
            assessmentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                backRouteName: 'cm.case.assessments'
            },
            documentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (document: CM.CaseDocument) => { document.case = { id: this.caseId }; }
            },
            notesConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (note: CM.CaseNote) => { note.case = { id: this.caseId }; }
            },
            correspondenceConfig: {
                transitionalPageId: '3079',
                moduleCode: 'CM',
                serviceRootUrl: 'cm/cases',
                rollupRootUrl: 'cm/cases/rollup/correspondence'
            },
            contactLogConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                rollupConfig: {
                    serviceRootUrl: 'cd/members',
                    targetType: 'CD.Member'
                }
            }
        }
    };


    private notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/careplans/interventions',
        targetType: 'CM.CarePlanIntervention',
        addTargetProperty: this.addInterventionProperty.bind(this),
        excludeSystem: false
    };

    private queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks',
        targetType: 'CM.Intervention'
    };

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly context: CasePageContext,
        private readonly service: TaskService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate({ caseId, goalId, taskId }: { caseId: string, goalId: string, taskId: string }): Promise<Redirect | true> {
        if(!(caseId && goalId && taskId)) {
            throw new Error('Required parameters [caseId] and/or [goalId] and/or [taskId] were not found');
        }

        try {
            this.caseId = caseId;
            this.goalId = goalId;
            this.interventionId = taskId;
            if(!this.context.case || this.context.case.id !== this.caseId) {
                await this.context.loadCase(this.caseId, undefined, ['number']);
            }

            this.service.configure(this.tasksConfig);
            const response = await this.service.loadTask(this.interventionId, undefined, ['case.id']);
            const intervention: CM.CarePlanIntervention = response.results[0];
            const interventionCaseId = intervention.case?.id;
            if(interventionCaseId !== this.caseId) {
                // reroute user if intervention does not belong to the case
                const messageOptions = {
                    what: this.i18n.tr('cm:carePlanIntervention.$'),
                    item: this.i18n.tr('cm:case.$').toLowerCase(),
                    number: this.context.case.number
                };
                const message = this.i18n.tr('ui:messages.errors.doesNotExistFor', messageOptions);
                this.toaster.error().message(message).show();
                return new Redirect('careplan');
            }
            return true;
        } catch(error) {
            throw error;
        }
    }

    protected async activate({ activeTab, from }: { activeTab?: string, from?: string }, routeConfig: RouteConfig): Promise<void> {
        this.memberId = this.context?.case?.member?.id || '';
        this.tasksConfig.memberId = this.memberId;
        this.tasksConfig.backRouteName = from || undefined;

        await this.accountContext.load();
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');

        this.taskBinding = {
            targetId: this.goalId,
            relatedModels: { 'CD.Member': this.memberId, 'CM.Case': this.caseId, 'CM.Goal': this.goalId },
            taskId: this.interventionId,
            smartTaskTargetId: this.caseId,
            tasksConfig: this.tasksConfig,
            notesConfig: this.notesConfig,
            queuesConfig: this.queuesConfig,
            readonly: this.context.caseIsReadOnly(canEditClosedCase),
            canDeactivateNotes: true,
            showAppointments: true,
            showTaskQueues: true,
            canUpdateTaskDueDate: true,
            showDelegateToQueue: true,
            showDelegateToUser: true,
            canSaveTask: true,
            activeTab: activeTab
        };

        this.mcgCopyright = getApiSetting(this.context.settings, 'mcg.informed.copyright.$display')?.toString();

        this.routeConfig = routeConfig;
    }

    ////****************** Events ****************************************************////
    protected titleReload(): void {
        if(this.routeConfig?.navModel) {
            this.routeConfig.navModel.setTitle(this.i18n.tr('cm:routes.interventions.$title', { what: this.taskDisplay }));
        }
    }


    ////****************** Functions *************************************************////
    public addGoalProperty(task: CM.CarePlanIntervention): void {
        task.goal = { id: this.goalId };
    }

    protected addCaseProperty(task: CM.CaseTask): void {
        task.case = { id: this.caseId };
    }

    public addInterventionProperty(model: CM.CarePlanInterventionNote | CM.CarePlanInterventionAppointment): void {
        model.intervention = { id: this.interventionId };
    }

}
